/**
 * Instance root merge (ADR-0013): an instance Node becomes the single-root
 * `.tscn` it instances. The tree, the viewport `NodeDispatcher` and
 * `resolveLiveEntry` all apply it, so node paths agree, which the
 * selection-driven Animation tab needs (ADR-0012).
 */
import { scopeOf } from './liveNode.js';
import { graftInstanceChildren } from './graftInstanceChildren.js';
import { mayHandBack, seatInInstance } from './seatEscaped.js';
import { withHandOffsSettled } from './ownerHandOff.js';
import { WAITING_FOR_SCENES } from './sceneTrail.js';
import { rehomeOverride } from './rehomeOverride.js';
import { layerRawOverride } from './layerRawOverride.js';
import { GLB_SCENE_ROOT_TYPE } from './glbSceneRoot.js';
import { cachedUniqueNamesBelowRoot } from '../utils/uniqueNames.js';
import { perObject } from '../utils/perObject.js';
/**
 * The instance node's keys were written against the outer scope, the root's against the
 * sub-scene's, so the instance's are re-homed into the sub-scene's before they layer on.
 * The sub-scene's scope replaces the instance node's own: the merged node sits inside it.
 */
function mergeProperties(instanceNode, root, scopes) {
    const rehomed = rehomeOverride(instanceNode.rawProperties, scopes.outer, scopes.content);
    return { ...layerRawOverride(instanceNode, root, rehomed.raw), scope: rehomed.scope };
}
/** The cache of a caller with none: a nested instance stays as it is, and a trail waits. */
const NO_CACHE = { collapse: (node) => node, followTrail: WAITING_FOR_SCENES };
/**
 * Fold a single-root `.tscn` sub-scene into its instance Node. Returns `null`
 * for a scene without exactly one top-level node, or a lone `GLBSceneRoot`, and
 * the caller keeps the nested-injection form. The merged Node takes the root's
 * `type`, `children` and `instance` ref. An inherited root arrives composed over its base
 * already, carrying the table that composition built, which the host's settings land on.
 *
 * `cache.collapse` collapses an instance node inside the merged tree: it gives the graft the table
 * of a nested instance, and seats the host children that a nested instance hands back.
 * `cache.followTrail` follows the rest of a `parent_id_path=` or `owner_uid_path=` that enters the
 * instance. The default leaves a nested instance as it is and makes a trail wait, for a caller
 * with no scene cache.
 */
export function mergeInstanceRoot(authored, loadedScene, scopes, { collapse, followTrail } = NO_CACHE) {
    if (loadedScene.nodes.length !== 1)
        return null;
    const root = loadedScene.nodes[0];
    // A GLB's instance children are overrides matched by name through `GlbInstanceProvider`,
    // which a collapse would discard.
    if (root.type === GLB_SCENE_ROOT_TYPE)
        return null;
    // A setting of this instance may await a host child that is an instance, as an inherited root's does.
    const instanceNode = withHandOffsSettled([authored], scopes.outer, collapse)[0];
    const merged = mergeProperties(instanceNode, root, scopes);
    // The graft below lands these settings. A root that inherits a base the cache could not
    // compose it over (the dispatcher's one-scene cache) carries its own file's settings on, so
    // the next collapse over that base lands them. A composed root carries none.
    const { instanceNameSettings: settings = [], seatedPlacements, ...host } = instanceNode;
    const composed = root.uniqueNames;
    const own = composed ? () => composed.paths() : () => cachedUniqueNamesBelowRoot(loadedScene.nodes);
    // A host child whose parent path descends into this instance is grafted at
    // the sub-path it names (`graftInstanceChildren`). Direct children append.
    const nested = perObject((node) => collapse(node, scopes.content).uniqueNames?.paths());
    const hasUnloadedBase = Boolean(root.instance) && composed === undefined;
    const graft = graftInstanceChildren(root.children, instanceNode.children, scopes, {
        own,
        settings,
        nested,
        hasUnloadedBase,
        followTrail,
        isInheritedRoot: instanceNode.parent === undefined,
    });
    const grafted = {
        ...host,
        type: root.type,
        // The instance node's own ref is consumed here. The tree's badge and
        // open-standalone affordance read the originating ref in the caller's scope.
        instance: root.instance,
        ...(root.instanceNameSettings ? { instanceNameSettings: root.instanceNameSettings } : {}),
        ...merged,
        children: graft.children,
        uniqueNames: graft.uniqueNames,
        uniqueParentPlacements: seatedPlacements
            ? new Map([...graft.uniqueParentPlacements, ...seatedPlacements])
            : graft.uniqueParentPlacements,
        ...(graft.escaped.length > 0 ? { escaped: graft.escaped } : {}),
        ...(graft.handOffDecisions.length > 0 ? { handOffDecisions: graft.handOffDecisions } : {}),
    };
    return mayHandBack(instanceNode) ? seatedInstance(grafted, scopes, collapse) : grafted;
}
/** `merged` with the host children that its nested instances hand back seated, or passed on in `escaped`. */
function seatedInstance(merged, scopes, collapse) {
    const table = merged.uniqueNames;
    const seated = seatInInstance({
        root: merged,
        scope: scopeOf(merged, scopes.content),
        claimed: (key) => table.paths().get(key),
        handedBack: [],
    }, collapse);
    if (seated.root === merged && seated.handOffDecisions.length === 0)
        return merged;
    const escaped = [...(merged.escaped ?? []), ...seated.escaped];
    const handOffDecisions = [...(merged.handOffDecisions ?? []), ...seated.handOffDecisions];
    return {
        ...seated.root,
        ...(escaped.length > 0 ? { escaped } : {}),
        ...(handOffDecisions.length > 0 ? { handOffDecisions } : {}),
    };
}
//# sourceMappingURL=mergeInstanceRoot.js.map